import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Survey } from 'freemium-survey-components';
import { Tooltip, Button, useModal, Modal, Loader } from 'freemium-ui';

import Icons from 'components/icons';
import { SurveyType } from 'shared/types';
import { FreshLink } from 'shared/globals';

export type PreviewProps = {
  values: SurveyType;
  placeholdersValues: any;
  accountInfo: any;
  expanded?: Boolean;
};

export const SurveyPreviewModal = ({
  values,
  placeholdersValues,
  accountInfo,
  expanded,
}: PreviewProps) => {
  const { setVisible, bindings } = useModal(false);
  const [mobile, setMobile] = React.useState(false);
  const [surveyRestartCount, setSurveyRestartCount] = useState(0); // counting it just to set the key to incrementing number so that it would remount
  const [animationEnds, setAnimationEnds] = useState(true); // the modal size animation from desktop (large) to mobile (small) and vice versa in freemium-ui hinders the height calculation algo for the question (which calcuates the height while the animtion is still in progress in which the width is not yet settled)
  const restartPreview = () => {
    setSurveyRestartCount(surveyRestartCount + 1);
  };
  return (
    <>
      {expanded ? (
        <Button type="secondary" onClick={() => setVisible(true)}>
          Preview
        </Button>
      ) : (
        <div
          aria-describedby="preview-control"
          onClick={() => setVisible(true)}
        >
          <Tooltip label="Preview" position="left">
            <Icons glyph="preview" size="22" viewBox="-8 -10 26 26" />
          </Tooltip>
        </div>
      )}
      <Modal
        {...bindings}
        size={mobile ? 'small' : 'large'}
        customModalContentStyles={{
          borderRadius: '4px',
          marginBottom: '48px',
        }}
      >
        <SurveyPreview
          key={surveyRestartCount}
          values={values}
          placeholdersValues={placeholdersValues}
          accountInfo={accountInfo}
          mobile={mobile}
          animationEnds={animationEnds}
          setAnimationEnds={setAnimationEnds}
          toggleMobileView={() => {
            setAnimationEnds(false);
            setMobile(!mobile);
          }}
          close={() => setVisible(false)}
          restartPreview={restartPreview}
        />
      </Modal>
    </>
  );
};

const SurveyPreview = ({
  close,
  mobile,
  toggleMobileView,
  values,
  placeholdersValues,
  accountInfo,
  restartPreview,
  animationEnds = true,
  setAnimationEnds,
  customStyles = {},
}: {
  customStyles?: any;
  animationEnds?: boolean;
  mobile: boolean;
  values: SurveyType;
  close?: Function;
  toggleMobileView?: Function;
  setAnimationEnds?: Function;
  placeholdersValues?: any;
  accountInfo?: any;
  restartPreview?: any;
}) => {
  const [isPreviewSurveyCompleted, setIsPreviewSurveyCompleted] =
    useState(false);

  const { ui_theme } = values;
  useEffect(() => {
    setTimeout(() => {
      setAnimationEnds?.(true);
    }, 500); // based on modal animation duration in the freemium-ui which is lesser than 500ms
  }, [mobile]);

  const logoUrl = values?.ui_branding?.logo_url ?? accountInfo?.logo_url;
  return (
    <StyledSurveyPreview
      className="survey-preview"
      style={
        ui_theme
          ? {
              ...((ui_theme.brand_color || accountInfo?.brand_color) && {
                '--brand-color':
                  ui_theme.brand_color || accountInfo?.brand_color || '#264966',
              }),
              ...(ui_theme.body_background_color && {
                '--background-color': ui_theme.body_background_color,
              }),
              ...(ui_theme.secondary_button_color && {
                '--secondary-button-color': ui_theme.secondary_button_color,
              }),
              ...(ui_theme.question_background_color && {
                '--question-background-color':
                  ui_theme.question_background_color,
              }),
              ...(ui_theme.primary_button_text_color && {
                '--button-text-color': ui_theme.primary_button_text_color,
              }),
              ...(ui_theme.secondary_button_text_color && {
                '--secondary-button-text-color':
                  ui_theme.secondary_button_text_color,
              }),
              ...(ui_theme.question_text_color && {
                '--question-text-color': ui_theme.question_text_color,
              }),
              backgroundColor: 'var(--background-color, #f6f8fa)',
              ...customStyles,
            }
          : {
              ...customStyles,
            }
      }
    >
      <div className="close" onClick={() => close?.()}>
        <Icons glyph="close-mini" size="18" viewBox="0 0 16 16" />
      </div>
      <br />
      <StyledSurvey>
        <header className="header">
          {logoUrl ? (
            <img src={logoUrl} alt="brand-logo" />
          ) : (
            <div className="company_name">
              {accountInfo?.company_name ?? values?.ui_branding?.name}
            </div>
          )}
          <div
            className={`description ${
              isPreviewSurveyCompleted ? 'hidden_description' : ''
            }`}
          >
            {values.header_message}
          </div>
        </header>
        <main className={`main ${animationEnds ? '' : 'loader'}`}>
          {animationEnds ? (
            <Survey
              survey={values}
              surveyStyle={values.meta?.survey_layout}
              placeholders={placeholdersValues}
              onSubmit={(
                data: any,
                callback: any,
                isPartialData: boolean = false,
              ) => {
                if (!isPartialData) {
                  callback?.();
                  setIsPreviewSurveyCompleted(true);
                }
              }}
              preview={true}
              closePreview={close}
              mobilePreview={mobile}
            />
          ) : (
            <Loader />
          )}
        </main>
        <footer className="preview-footer">
          <FreshLink
            href="https://www.freshworks.com/?utm_source=survey&utm_medium=survey_public_page"
            target="_blank"
            rel="noreferrer"
          >
            NPS survey powered by &nbsp;
            <span className="logo">
              <img
                src="https://assets.freshsurvey.io/email/freshworks.svg"
                alt="Freshworks Logo"
                width={80}
                height={20}
              />
            </span>
          </FreshLink>
        </footer>
      </StyledSurvey>

      <div className="toggle-preview">
        <div className="controls">
          <Tooltip label="Desktop view" position="bottom">
            <div
              className={`control ${mobile ? '' : 'active'}`}
              onClick={() => {
                toggleMobileView?.(false);
                setIsPreviewSurveyCompleted(false);
              }}
            >
              <Icons glyph="desktop" size="18" viewBox="0 0 16 14" />
            </div>
          </Tooltip>
          <Tooltip label="Mobile view" position="bottom">
            <div
              className={`control ${mobile ? 'active' : ''}`}
              onClick={() => {
                toggleMobileView?.(true);
                setIsPreviewSurveyCompleted(false);
              }}
            >
              <Icons glyph="mobile" size="16" viewBox="0 0 9 14" />
            </div>
          </Tooltip>
        </div>
        <div className="controls">
          <Tooltip label="Restart preview" position="bottom">
            <div
              className={`control active`}
              onClick={() => restartPreview?.()}
            >
              <Icons glyph="restart" size="16" viewBox="0 0 15 12" />
            </div>
          </Tooltip>
        </div>
      </div>
    </StyledSurveyPreview>
  );
};

const StyledSurveyPreview = styled.div`
  min-height: 500px;
  max-height: 85vh;
  height: 700px;
  background-color: #f6f8fa;
  overflow: auto;
  border-radius: 4px;
  position: relative;
  button {
    &:focus {
      outline: 2px solid #005fcc !important;
    }
  }
  .close {
    position: fixed;
    right: 10px;
    top: 5px;
    cursor: pointer;
  }

  .brand-logo {
    width: 120px;
  }
  .description {
    font-size: 13px;
    color: #666;
    white-space: pre-wrap;
    line-height: 1.8;
    max-width: min(100%, 520px);
    text-align: center;
    margin-bottom: 20px;
    padding: 0 20px;
    opacity: 1;
  }
  .hidden_description {
    opacity: 0;
  }
  .toggle-preview {
    display: flex;
    position: fixed;
    bottom: -50px;
    z-index: 10;
    width: 100%;
    align-items: center;
    justify-content: center;

    .controls {
      display: flex;
      align-items: center;
      box-shadow: 0px 2px 16px rgba(18, 52, 77, 0.1);
      border-radius: 4px;
      margin: 0 5px;
      overflow: hidden;
      height: 40px;
      padding: 8px 0;
      background-color: #fff;

      .control {
        background: #ffffff;
        padding: 0 16px;
        cursor: pointer;
        display: flex;
        align-items: center;
        color: #a8a8a8;

        &:first-child {
          border-right: 1px solid var(--color-smoke-100);
        }

        &.active {
          color: #12344d;
        }
      }
    }
  }
`;

const StyledSurvey = styled.div`
  /* max-height: calc(100vh - 180px); */
  overflow-y: auto;
  /* height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center; */

  p {
    font-weight: inherit;
    margin: 0;
    font-size: inherit;
  }

  //copy the styles from public page to here
  .header {
    display: flex;
    flex-direction: column;
    align-items: center;
    .description {
      padding-bottom: 15px;
    }
  }
  .header .company_name {
    margin-top: 15px;
    margin-left: 10px;
    margin-bottom: 10px;
    font-weight: 600;
    font-size: 24px;
    line-height: 29px;
    color: #12344d;
    &:empty {
      display: none;
    }
  }
  .header img {
    height: 100px;
    width: 200px;
    object-fit: contain;
    margin-bottom: 20px;
  }

  .main {
    /* flex-grow: 1; */
    padding: 40px;
    padding-top: 0;
    min-height: 350px;
    max-width: 700px;
  }
  .loader {
    align-items: center;
    display: flex;
    justify-content: center;
  }

  .preview-footer {
    font-size: 12px;
    color: #606e7b;
    display: flex;
    padding: 4px 0;
    justify-content: center;
    align-items: center;
    position: fixed;
    bottom: 0;
    width: 100%;
    background-color: #fff;
    border-radius: 4px;
  }

  .preview-footer a {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-grow: 1;
    line-height: 1;
  }

  .title,
  .description {
    text-align: center;
    max-width: 480px;
    color: var(--color-smoke-800);
    font-size: 16px;
    line-height: 1.8;
    font-weight: 500;
    text-align: center;
    align-items: center;
    justify-content: center;
  }
`;
export default SurveyPreview;
